import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  fadeSpeed: number;
  hue: number;
}

interface SparkleBackgroundProps {
  className?: string;
  particleCount?: number;
  minSize?: number;
  maxSize?: number;
  speed?: number;
  colors?: 'brand' | 'blue' | 'purple' | 'white';
  children?: React.ReactNode;
}

export function SparkleBackground({
  className,
  particleCount = 50,
  minSize = 1,
  maxSize = 3,
  speed = 0.5,
  colors = 'brand',
  children,
}: SparkleBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  // Store CSS dimensions to use for particle coordinate math (not device pixels)
  const cssDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  const getColorHue = useCallback((colorScheme: string): { min: number; max: number } => {
    switch (colorScheme) {
      case 'blue':
        return { min: 210, max: 230 };
      case 'purple':
        return { min: 260, max: 290 };
      case 'white':
        return { min: 0, max: 0 };
      case 'brand':
      default:
        return { min: 210, max: 290 };
    }
  }, []);

  const createParticle = useCallback(
    (cssWidth: number, cssHeight: number): Particle => {
      const { min, max } = getColorHue(colors);
      return {
        x: Math.random() * cssWidth,
        y: Math.random() * cssHeight,
        size: Math.random() * (maxSize - minSize) + minSize,
        speedX: (Math.random() - 0.5) * speed,
        speedY: (Math.random() - 0.5) * speed,
        opacity: Math.random(),
        fadeSpeed: Math.random() * 0.02 + 0.005,
        hue: colors === 'white' ? 0 : Math.random() * (max - min) + min,
      };
    },
    [colors, maxSize, minSize, speed, getColorHue]
  );

  const initParticles = useCallback(
    (cssWidth: number, cssHeight: number) => {
      particlesRef.current = Array.from({ length: particleCount }, () =>
        createParticle(cssWidth, cssHeight)
      );
    },
    [particleCount, createParticle]
  );

  const updateParticle = useCallback(
    (particle: Particle, cssWidth: number, cssHeight: number): Particle => {
      let { x, y, opacity, fadeSpeed } = particle;
      const { speedX, speedY } = particle;

      x += speedX;
      y += speedY;
      opacity += fadeSpeed;

      if (opacity >= 1 || opacity <= 0) {
        fadeSpeed = -fadeSpeed;
        opacity = Math.max(0, Math.min(1, opacity));
      }

      // Wrap around using CSS pixel dimensions
      if (x < 0) x = cssWidth;
      if (x > cssWidth) x = 0;
      if (y < 0) y = cssHeight;
      if (y > cssHeight) y = 0;

      return { ...particle, x, y, opacity, fadeSpeed };
    },
    []
  );

  const drawParticle = useCallback(
    (ctx: CanvasRenderingContext2D, particle: Particle) => {
      const { x, y, size, opacity, hue } = particle;

      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);

      if (colors === 'white') {
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.8})`;
      } else {
        ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${opacity * 0.8})`;
      }

      ctx.fill();

      // Add glow effect
      ctx.beginPath();
      ctx.arc(x, y, size * 2, 0, Math.PI * 2);
      if (colors === 'white') {
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.2})`;
      } else {
        ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${opacity * 0.2})`;
      }
      ctx.fill();
    },
    [colors]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      const { width, height } = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // Store CSS dimensions for particle coordinate math
      cssDimensionsRef.current = { width, height };
      // Set canvas size to device pixels for sharp rendering
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      // Scale context so drawing operations use CSS pixels
      ctx.scale(dpr, dpr);
      // Initialize particles using CSS pixel dimensions
      initParticles(width, height);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const animate = () => {
      if (!canvas || !ctx) return;

      const { width: cssWidth, height: cssHeight } = cssDimensionsRef.current;

      // Clear using CSS pixel dimensions (context is scaled)
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      // Update particles using CSS pixel dimensions
      particlesRef.current = particlesRef.current.map((particle) =>
        updateParticle(particle, cssWidth, cssHeight)
      );

      particlesRef.current.forEach((particle) => {
        drawParticle(ctx, particle);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initParticles, updateParticle, drawParticle]);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      />
      {children && <div className="relative z-10">{children}</div>}
    </div>
  );
}

export default SparkleBackground;
